import { useState, useRef, useCallback } from 'react'
import { api } from '../utils/api'
import { useEditorStore } from '../store/editor.store'
import { useNavigate } from 'react-router-dom'

// ── §65: Screenshot → Website ─────────────────────────────────────────────────

interface GeneratedResult {
  title: string
  sections: Array<{ id: string; blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }>
  themeConfig: Record<string, string>
  analysis: string
}

interface Props {
  onClose: () => void
  siteId?: string | null
  onCreated?: () => void
}

export function ScreenshotCreateModal({ onClose, onCreated }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { initFromApiSite, addSection, addBlock, updatePage, updateSettings } = useEditorStore()

  const handleFile = useCallback((f: File) => {
    if (!f.type.startsWith('image/')) { setError('Только изображения (PNG, JPG, WebP)'); return }
    if (f.size > 10 * 1024 * 1024) { setError('Файл слишком большой (макс. 10 МБ)'); return }
    setFile(f)
    setError('')
    const reader = new FileReader()
    reader.onload = e => setPreview(e.target?.result as string)
    reader.readAsDataURL(f)
  }, [])

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  async function handleGenerate() {
    if (!file || !preview) return
    setLoading(true)
    setError('')
    try {
      // Extract base64 (strip data:image/...;base64, prefix)
      const base64 = preview.split(',')[1]
      const mimeType = file.type

      const result = await api.post<GeneratedResult>('/ai/generate-from-screenshot', {
        imageBase64: base64,
        mimeType,
        description: description.trim() || undefined,
      })

      // Apply result to editor store
      initFromApiSite({ id: '', name: result.title, slug: '', status: 'DRAFT', pages: [] })
      updatePage({ title: result.title })

      if (result.sections?.length) {
        for (const sec of result.sections) {
          addSection()
          const newSec = useEditorStore.getState().page.sections.at(-1)
          if (newSec) {
            for (const block of sec.blocks) {
              addBlock(newSec.id, block.type, block.data)
            }
          }
        }
      }

      if (result.themeConfig) {
        updateSettings({ themeConfig: JSON.stringify(result.themeConfig) })
      }

      onCreated?.()
      onClose()
      navigate('/editor')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка генерации')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', borderRadius: 16, width: '100%', maxWidth: 560, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>🖼️ Сайт из скриншота</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Загрузите скриншот сайта — AI воссоздаст его структуру</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--accent)' : preview ? '#22c55e' : 'var(--border)'}`,
              borderRadius: 12, padding: preview ? 0 : '40px 20px',
              textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'var(--bg-2)' : 'transparent',
              transition: 'all 0.15s', overflow: 'hidden', marginBottom: 14,
            }}
          >
            {preview ? (
              <div style={{ position: 'relative' }}>
                <img src={preview} alt="preview" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', display: 'block' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.4)')}
                  onMouseLeave={e => ((e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0)')}
                >
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, opacity: 0 }}
                    onMouseEnter={e => ((e.currentTarget as HTMLElement).style.opacity = '1')}
                    onMouseLeave={e => ((e.currentTarget as HTMLElement).style.opacity = '0')}
                  >Нажмите чтобы заменить</span>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🖼️</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                  Перетащите скриншот сюда
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>или нажмите для выбора файла · PNG, JPG, WebP · до 10 МБ</div>
              </>
            )}
          </div>

          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />

          {/* Description */}
          <textarea
            placeholder="Описание (необязательно): 'Сайт IT-компании, тёмная тема, premium стиль...'"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-2)', color: 'var(--text-primary)', resize: 'none', boxSizing: 'border-box', marginBottom: 14 }}
          />

          {error && (
            <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 7, fontSize: 12, marginBottom: 12 }}>
              {error}
            </div>
          )}

          {/* AI info */}
          <div style={{ padding: '10px 12px', background: 'var(--bg-2)', borderRadius: 8, fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
            <strong>Как это работает:</strong> AI анализирует скриншот через Claude Vision — извлекает цветовую палитру, типографику, структуру секций — и генерирует сайт на платформе BST.
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '9px 18px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 600 }}>
              Отмена
            </button>
            <button
              onClick={() => void handleGenerate()}
              disabled={!file || loading}
              style={{ padding: '9px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: file && !loading ? 'pointer' : 'not-allowed', opacity: file && !loading ? 1 : 0.6 }}
            >
              {loading ? '⏳ Анализируем...' : '✨ Сгенерировать сайт'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
